import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { LoadingBlock } from '../components/ui.tsx';
import { useSettingsHashJump } from '../components/settings-nav.tsx';
import { SettingsLeaf as SettingsLeafFrame } from './settings/settings-leaf.tsx';
import { settingsSectionDef, useSettingsSectionContext } from '../components/settings/settings-registry.tsx';
import {
  settingsLeafPath,
  settingsLegacyPathForSlug,
  settingsSectionFromHash,
  settingsSectionIdFromSlug,
  settingsSectionOwner,
  settingsSectionSlug,
  type SettingsSectionId,
} from '../components/settings/settings-routes.ts';

/**
 * `/settings/<section>`: one Settings section on a page of its own.
 *
 * For a company whose Settings is still one long page, this URL is sent on
 * to that section's anchor there, so a link can name the new address before
 * the hub is switched on. On the hub, a section this person cannot see —
 * a permission or the plan hides it, exactly as the long page did — and a
 * name that is no section at all both go back to the hub rather than to an
 * empty page.
 */
export default function SettingsLeafPage() {
  const { t } = useTranslation();
  const { sectionSlug = '' } = useParams();
  const { search, hash } = useLocation();
  const { company } = useSession();

  if (!company) return <LoadingBlock label={t('settings.loadingSettings')} />;
  if (company.settingsHub !== true) {
    return <Navigate to={settingsLegacyPathForSlug(sectionSlug, search, hash)} replace />;
  }

  const id = settingsSectionIdFromSlug(sectionSlug);
  if (!id) return <Navigate to="/settings" replace />;
  // A section with a page of its own (pricing, team, account, …) opens it;
  // a camelCase id becomes its kebab-case address.
  if (settingsSectionOwner(id) !== id) {
    const target = settingsLeafPath(id, search);
    // A section with a page of its own is matched by that page's route
    // before this one; were it ever not, going back to the hub beats
    // redirecting to the same address forever.
    return <Navigate to={target.startsWith(`/settings/${sectionSlug}`) ? '/settings' : target} replace />;
  }
  if (sectionSlug !== settingsSectionSlug(id)) {
    return <Navigate to={`/settings/${settingsSectionSlug(id)}${search}${hash}`} replace />;
  }
  return <SettingsLeaf key={id} id={id} />;
}

function SettingsLeaf({ id }: { id: SettingsSectionId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, hash } = useLocation();
  const { context, ready } = useSettingsSectionContext();

  // A nested anchor (`/settings/account#integrations`, the mailbox return;
  // `/settings/pricing#settings-numbering`) scrolls into view once the
  // cards exist, exactly as it did on the long page.
  useSettingsHashJump(ready);

  // Cards still carry in-page links to other sections (`#settings-billing`
  // under an upgrade note). On the long page those scrolled; here the
  // section is somewhere else, so open its page instead. The anchor's own
  // history entry is replaced, so Back returns to this section.
  useEffect(() => {
    const target = settingsSectionFromHash(hash);
    if (target && settingsSectionOwner(target) !== id) navigate(settingsLeafPath(target, search), { replace: true });
  }, [hash, search, id, navigate]);

  const def = settingsSectionDef(id);
  if (!def?.Component) return <Navigate to="/settings" replace />;
  if (!context) return <LoadingBlock label={t('settings.loadingSettings')} />;
  if (!def.when(context)) {
    // Job chat and Refer & Earn are only known once their queries answer.
    return ready ? <Navigate to="/settings" replace /> : <LoadingBlock label={t('settings.loadingSettings')} />;
  }

  const { Component } = def;
  return (
    <SettingsLeafFrame title={t(def.titleKey)}>
      {/* The same anchor the long page wraps this section in: keyboard focus
          lands here on a jump, and `#settings-<id>` still finds it. */}
      <div id={`settings-${id}`} tabIndex={-1} className="outline-none">
        <Component />
      </div>
    </SettingsLeafFrame>
  );
}
